export type PromptDefinition = {
    id: string;
    seoTitle: string;
    seoDescription: string;
    emoji: string;
    type: string;
    title: string;
    description: string;
    example: string;
    textLabel: string;
    textPlaceholder: string;
    showToneSelector: boolean;
    prompt: (arg0: any) => string;
};

const promptTemplate = ({
    prompt,
    language = 'english',
    tone = 'professional',
    length = 'short',
}) => {
    return `${prompt}. Use ${tone} tone. Reply must be in ${language} language. The length of the reply must be ${length}. `;
};

export const prompts: Array<PromptDefinition> = [
    {
        id: 'reply-chat',
        emoji: '🙌',
        type: 'chat',
        seoTitle: 'AI Chat Message Reply Generator',
        seoDescription:
            'Reply to your chat messages in just couple clicks. AI Perfect Assistant creates logical and structured answers to any type of messages you get in your chats or groups. Keep track of all the events without spending too much time on creating a perfect reply with our AI powered tool.',
        title: 'Reply to Chat Message',
        example:
            "Hello, team! Could someone, please, share the documents for today's meeting?",
        description: 'Generate a reply to message that you received in chat',
        prompt: (args) => {
            const { text } = args;
            return promptTemplate({
                ...args,
                prompt: `Create a reply to the chat message that I gave you in <>. It should be done to send in as a chat message. <${text}>`,
            });
        },
        textLabel: 'What message we should generate reply for?',
        textPlaceholder:
            'Type your message here to generate a reply with AI Perfect Assistant',
        showToneSelector: true,
    },
    {
        id: 'birthday',
        type: 'chat',
        emoji: '🎂',
        seoTitle: 'AI Birthday Wishes Generator',
        seoDescription:
            'Make your dearest people feel special on their special day. Get them a unique Birthday wish. AI Perfect Assistant will generate birthday wishes that are more meaningful and heartfelt, as well as more humorous and light-hearted. Share your wishes with others, spread joy and cheer, no more need to worry about putting your feelings into words - AI Perfect Assistant got you.',
        example:
            'Generate a wish for the boss of the team, highlight the best qualities the boss could have.',
        title: 'Birthday Wish',
        description: 'Send sincere birthday wish for your loved ones.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Produce a birthday wish for the person based on person description that I gave you in <>. It should be done to send in as a chat message. It should not include mine name.  <${args.text}>`,
                ...args,
            }),
        textLabel:
            'Whose birthday is it? Give some information about the person',
        textPlaceholder:
            'Type here the name and age of the person who is celebrating their birthday.',
        showToneSelector: true,
    },
    {
        id: 'apology',
        type: 'chat',
        seoTitle: 'AI Apologize Generator',
        seoDescription:
            'Express your regret and remorse in a more meaningful way. Generate an apology that is truly sincere, heartfelt and appropriate for the situation. AI Perfect Assistant create personalized apologies that are tailored to the individual receiving them.',
        emoji: '😥',
        title: 'Apologize',
        example: 'Being late for the weekly meeting',
        description: 'Apologize for the mistake you have done.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Produce an apology based on mistake that I gave you in <>. Apologize as I. It should be done to send in as a chat message.  <${args.text}>`,
                ...args,
            }),
        textLabel: 'What is this about?',
        textPlaceholder: 'Type here for what you would like to apologize',
        showToneSelector: true,
    },
    {
        id: 'greeting',
        type: 'chat',
        emoji: '👋',
        seoTitle: 'AI Greeting Generator',
        seoDescription:
            'Provide a warm and welcoming introduction to the new employee. Personalize greeting that is tailored to the individual, provide helpful information about the company. All can be done in couple clicks with AI Perfect Assistant.',
        title: 'Greet New Employee',
        example: 'The new employee of the finance department of the bank',
        description: 'Write the perfect welcome message for a new employee.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Write a perfect welcome message for the new employee based on the data that I gave you in <>. It should be done to send in as a chat message. <${args.text}>`,
                ...args,
            }),
        textLabel: 'Type a few sentence about new employee',
        textPlaceholder: 'Type here any details about new employee',
        showToneSelector: true,
    },
    {
        id: 'invitation',
        type: 'chat',
        emoji: '📩',
        seoTitle: 'AI Event Invitation Generator',
        seoDescription:
            'Create an eye-catchy, creative and engaging invitation to any event you wish! AI Perfect Assistant will express all your feelings and clear all event detail quickly and easily with a perfect invite. Treat your guests specially with customized invitations.',
        title: 'Invitation To Event',
        example: 'A charity fundraising dinner',
        description: 'Write the perfect event invitation.',
        prompt: (args) =>
            promptTemplate({
                prompt: ` Write a perfect invitation for the event based on data that I gave you in <>. It should be done to send in as a chat message.  <${args.text}>`,
                ...args,
            }),
        textLabel: 'What is the event?',
        textPlaceholder: 'Type here details about the event',
        showToneSelector: true,
    },
    {
        id: 'mail-improve-draft',
        type: 'mail',
        example: `Hi, how're you? is there any update on our talk yesterday? 
        Make it polite, formal and readable.`,
        emoji: '📝',
        seoTitle: 'AI Tool to Improve your drafts',
        seoDescription:
            "Improve your pieces of writing easily. There's no need to rack your brain over style or structure. Let AI organize your text within couple clicks to get you a perfectly structured email, report or document.",
        title: 'Improve Draft',
        description:
            'Improve draft of the message, make it readable, correct typos and grammar.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Based on the text draft that I gave you in <> create an easy to digest text, make it more readable. fix grammar and typos. stay close to original. Draft: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What draft you would like to improve?',
        textPlaceholder:
            'Type here a draft of the email message that you would like to improve',
        showToneSelector: true,
    },
    {
        id: 'mail-reply',
        type: 'mail',
        emoji: '💌',
        seoTitle: 'AI Reply Generator',
        seoDescription:
            'Automate your process of responding to messages, saving time and resources. AI Perfect Assistant will provide you with high quality quick responses to any message: from personal to business.',
        title: 'Reply To Message',
        example: `Dear Ms. Chu,

        On behalf of Northwest Innovative Consulting, it is my pleasure to inform you we would like to accept Excellent Quality IT Company's proposal for the installation of our new computer system.
        
        Excellent Quality IT's proposal was exceptionally well-tailored to the needs we described in our initial meeting. We feel confident that your business will provide us with the exceptionally safe and high-speed information infrastructure we expect.
        
        We would like to finalize the contract and have you begin work by the end of March. Before then, we would appreciate the opportunity to review some budget items and confirm a mutually acceptable deliverable schedule. To arrange a meeting, please call our project lead at 888-888-8888.
        
        Excellent Quality IT has been a fantastic partner in determining the products and services our company needs. We so appreciate how prompt and courteous you have been in all your communications. Please let us know if there is anything you require from us ahead of our meeting.
        
        Best regards,
        Richard Smith`,
        description: 'Generate a reply to email message.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Create a perfect reply to the email message that I gave you in <>. Return only reply, do not add subject. Message: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What message we should generate reply for?',
        textPlaceholder:
            'Type your message here to generate a reply with AI Perfect Assistant',
        showToneSelector: true,
    },
    {
        id: 'positive-reply-customer-email',
        type: 'mail',
        example: `Hello! We're a non profit organization and would like to get a discount. Is it possible?`,
        emoji: '💚',
        seoTitle: 'AI Positive Reply Generator',
        seoDescription: `Transform your email responses with AI Perfect Assistant, leveraging ChatGPT to generate positive replies seamlessly within Microsoft 365 and Chrome. Integrated directly into Word, PowerPoint, Outlook, and your browser, our app ensures you convey the perfect tone in every email. Ideal for professionals looking to enhance communication efficiency and maintain positivity in their digital interactions`,
        title: 'Positive Reply',
        description: 'Generate a Positive Reply to the Email Message.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Act as a professional email support specialist. Based on the email message that I gave you in <>. Generate a positive reply, where you agree with what stated in the message. Return only reply, do not add subject. <${args.text}>`,
                ...args,
            }),
        textLabel:
            'Paste an email message that you would like to generate reply for',
        textPlaceholder:
            'Paste your email message here, and AI Perfect Assistant will generate a reply.',
        showToneSelector: true,
    },
    {
        id: 'negative-reply-customer-email',
        type: 'mail',
        example: `Hello! We're a non profit organization and would like to get a discount. Is it possible?`,
        emoji: '❌  ',
        seoTitle: 'AI Negative Reply Generator',
        seoDescription: `Transform your email responses with AI Perfect Assistant, leveraging ChatGPT to generate negative replies seamlessly within Microsoft 365 and Chrome. Integrated directly into Word, PowerPoint, Outlook, and your browser, our app ensures you convey the perfect tone in every email. Ideal for professionals looking to enhance communication efficiency in their digital interactions`,
        title: 'Negative Reply',
        description: 'Generate a Negative Reply to the Email Message.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Act as a professional email support specialist. Based on the email message that I gave you in <>. Generate a negative reply, where you do not agree with what stated in the message. Return only reply, do not add subject. <${args.text}>`,
                ...args,
            }),
        textLabel:
            'Paste an email message that you would like to generate reply for',
        textPlaceholder:
            'Paste your email message here, and AI Perfect Assistant will generate a reply.',
        showToneSelector: true,
    },
    {
        id: 'angry-customer-email',
        type: 'mail',
        example: `I am writing to express my extreme dissatisfaction with the service I have received from your company. I recently purchased a product and have been extremely disappointed with the results.`,
        emoji: '😡',
        seoTitle: 'AI Angry Customer Reply Generator',
        seoDescription: `Everyone who runs or operates a business will eventually receive an angry customer email. They’re inevitable. The trick is to write the perfect response to calm them down and convince them you’re solving their problems. Unfortunately, when emotion enters the picture, that’s hard to do. But what if there was an emotionless bot that could pen responses for you? Say hello to AI Perfect Assistant. The AI-powered algorithm can reply politely to customers in seconds, no matter how apoplectic they are.`,
        title: 'Angry Customer Email Reply',
        description: 'Write Email Responses to Angry Customers.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Act as a professional email support specialist. Based on the email from angry customer I gave you in <>. Generate a positive reply. Return only reply, do not add subject. <${args.text}>`,
                ...args,
            }),
        textLabel: 'Paste an email from angry customer',
        textPlaceholder:
            'Paste your email here, and AI Perfect Assistant will write a reply that will win them over and turn the situation into a positive one.',
        showToneSelector: true,
    },
    {
        id: 'mail-subject-line-creator',
        type: 'mail',
        example: `Onboarding email for SaaS product.`,
        emoji: '🦑',
        seoTitle: 'AI Email Subject Line Generator',
        seoDescription: `The most critical metric in any email marketing campaign is the open rate. You want to make sure as many people consume your content as possible. But, as any seasoned marketer will tell you, that’s anything but easy. Most people ignore marketing emails, relegating them to “promotions” or spam folders, never to be seen again. That’s why AI Perfect Assistant offers a email subject line generator. This tool makes it easy to concoct highly-clickable headlines that drive open rates and get conversions.`,
        title: 'Email Subject Line Creator',
        description:
            'Create highly-clickable headlines that drive open rates and get conversions.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Based on the email description that I gave you in <>. Generate a list of subject lines with the best possible open rate. <${args.text}>`,
                ...args,
            }),
        textLabel: 'What your email is about?',
        textPlaceholder:
            'Describe what your email is about and get a list of clickable subject lines',
        showToneSelector: true,
    },
    {
        id: 'write-paragraph',
        type: 'content',
        emoji: '✍️',
        seoTitle: 'AI Paragraph Generator',
        seoDescription:
            'Writing effective copies is essential in business, education and other spheres of life. Let AI Perfect Assistant create a logical, coherent paragraph devoted to any wished topic. Express your thoughts freely and get the excellent result in couple clicks.',
        title: 'Write a paragraph',
        example:
            'Generate the set of questions for the HR department to interview the new employee',
        description: 'Generate well-written paragraphs on any given subject.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Write a best possible paragraph on topic in <>. Topic: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What is you paragraph about?',
        textPlaceholder:
            'Type here a brief instruction on what we should generate paragraph',
        showToneSelector: true,
    },
    {
        id: 'write-resume',
        type: 'business',
        emoji: '📇',
        seoTitle: 'AI CV / Resume Generator',
        seoDescription:
            'Elevate your resume with AI Perfect Assistant, seamlessly integrated into Microsoft 365. Access AI-driven resume assistance directly in Word, PowerPoint, Outlook, and more, for standout, professional applications. Simplify your resume creation with personalized, industry-specific guidance in your favorite Microsoft tools.',
        title: 'Write a CV / Resume',
        example:
            'My name is Ilia, I am 30 years old. I am software engineer with 10 years experience in building web apps. My preferred stack is node.js, javascript, react.',
        description:
            'Simplify your resume creation with personalized, industry-specific guidance.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Write a professional resume template based on the information about person that I gave you in <>. Leave placeholders with guidance if you do not have enough info. Person information: <${args.text}>`,
                ...args,
            }),
        textLabel:
            'Write a few sentences about you, your skills and experience.',
        textPlaceholder:
            'Type here a brief summary about you, your skills and experience, we will use this data to generate outstanding resume.',
        showToneSelector: true,
    },
    {
        id: 'write-cover-letter',
        type: 'business',
        emoji: '📩',
        seoTitle: 'AI Cover Letter Generator',
        seoDescription:
            'Boost your job applications with AI Perfect Assistant, now integrated into Microsoft 365 and available as a Chrome extension. Effortlessly create tailored cover letters in Word, PowerPoint, Outlook, and directly from your browser, aligning perfectly with job listings for impactful applications.',
        title: 'Write Cover Letter with AI',
        example:
            'Microsoft seeks a Full Stack Node.js Developer to join our tech-driven team. Ideal for creative problem-solvers eager to impact our web application projects with Node.js, Express, and React expertise.',
        description: 'Effortlessly create tailored cover letters.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Write a professional cover letter template based on the job listing that I gave you in <>. Leave placeholders with guidance if you do not have enough info. Job listing: <${args.text}>`,
                ...args,
            }),
        textLabel: 'Paste here a job listing details',
        textPlaceholder:
            'Type here a job listing details, we will use this data to generate impactful cover letter.',
        showToneSelector: true,
    },
    {
        id: 'summarize',
        type: 'content',
        emoji: '💬',
        title: 'Summarize',
        seoTitle: 'AI Text Summarizer',
        seoDescription:
            'Short of time or just not feeling like reading an extensive file? Let AI Perfect Assistant help you out! It can help you in extracting key points from any piece of text and facilitate focusing on the main ideas and facts.',
        example: `Artificial Intelligence (A.I.) is a multidisciplinary field whose goal is to automate activities that presently require human intelligence. Recent successes in A.I. include computerized medical diagnosticians and systems that automatically customize hardware to particular user requirements. The major problem areas addressed in A.I. can be summarized as Perception, Manipulation, Reasoning, Communication, and Learning. Perception is concerned with building models of the physical world from sensory input (visual, audio, etc.). Manipulation is concerned with articulating appendages (e.g., mechanical arms, locomotion devices) in order to effect a desired state in the physical world. Reasoning is concerned with higher level cognitive functions such as planning, drawing inferential conclusions from a world model, diagnosing, designing, etc. Communication treats the problem understanding and conveying information through the use of language. Finally, Learning treats the problem of automatically improving system performance over time based on the system's experience. Many important technical concepts have arisen from A.I. that unify these diverse problem areas and that form the foundation of the scientific discipline. Generally, A.I. systems function based on a Knowledge Base of facts and rules that characterize the system's domain of proficiency. The elements of a Knowledge Base consist of independently valid (or at least plausible) chunks of information. The system must automatically organize and utilize this information to solve the specific problems that it encounters. This organization process can be generally characterized as a Search directed toward specific goals. The search is made complex because of the need to determine the relevance of information and because of the frequent occurrence of uncertain and ambiguous data. Heuristics provide the A.I. system with a mechanism for focusing its attention and controlling its searching processes.`,
        description: 'Extract key points from long text.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Summarize a paragraph of text in <> to several sentences. Text: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What would you like to summarize?',
        textPlaceholder: 'Type here a text that you would like to summarize',
        showToneSelector: true,
    },
    {
        id: 'fix-grammar',
        type: 'content',
        emoji: '✅',
        seoTitle: 'AI Tool to fix grammar and spelling',
        seoDescription:
            'Check your documents for accuracy and consistency, and get suggestions on alternative words or phrases to improve the clarity of the text. Fix your copies with ease using AI Perfect Assistant. No more need to worry about text accuracy or grammar slips. ',
        title: 'Fix spelling & grammar',
        example: `I goed to the store to buy some food, but i forgoting my wallet. I was so med at myself becuase i had to go back home to get it. I was so anoyed that i had to go back home and get it, but i was glad that i did becuase i am got to eat something.`,
        description: 'Check grammar of the text and fix any typos.',
        prompt: (args) =>
            `Check and fix a grammar and typos of a paragraph of text in <>. Do not change original text, only fix typos and grammar. Text: <${args.text}>`,
        textLabel: 'What text you would like to improve?',
        textPlaceholder: 'Type here a text that you would like to improve',
        showToneSelector: false,
    },
    {
        id: 'simplify',
        type: 'content',
        emoji: '🙂',
        seoTitle: 'AI tool to simplify text',
        seoDescription:
            'Stop racking your brain over another sophisticated piece of writing. AI Perfect Assistant will in one click simplify the text and highlight main ideas, so it can be understandable for everyone right away. Save time and energy on decoding difficult copies and focus on more important things.',
        title: 'Simplify language',
        example: `Artificial Intelligence (A.I.) is a multidisciplinary field whose goal is to automate activities that presently require human intelligence. Recent successes in A.I. include computerized medical diagnosticians and systems that automatically customize hardware to particular user requirements. The major problem areas addressed in A.I. can be summarized as Perception, Manipulation, Reasoning, Communication, and Learning. Perception is concerned with building models of the physical world from sensory input (visual, audio, etc.). Manipulation is concerned with articulating appendages (e.g., mechanical arms, locomotion devices) in order to effect a desired state in the physical world. Reasoning is concerned with higher level cognitive functions such as planning, drawing inferential conclusions from a world model, diagnosing, designing, etc. Communication treats the problem understanding and conveying information through the use of language. Finally, Learning treats the problem of automatically improving system performance over time based on the system's experience. Many important technical concepts have arisen from A.I. that unify these diverse problem areas and that form the foundation of the scientific discipline. Generally, A.I. systems function based on a Knowledge Base of facts and rules that characterize the system's domain of proficiency. The elements of a Knowledge Base consist of independently valid (or at least plausible) chunks of information. The system must automatically organize and utilize this information to solve the specific problems that it encounters. This organization process can be generally characterized as a Search directed toward specific goals. The search is made complex because of the need to determine the relevance of information and because of the frequent occurrence of uncertain and ambiguous data. Heuristics provide the A.I. system with a mechanism for focusing its attention and controlling its searching processes.`,
        description: 'Make text accessible to everyone.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Improve a paragraph of text in <> make it simpler to understand and read. Text should be understandable for five years old kid. Text: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What would you like to simplify?',
        textPlaceholder: 'Type here a text that you would like to simplify',
        showToneSelector: true,
    },
    {
        id: 'paraphrase',
        type: 'content',
        emoji: '👌',
        seoTitle: 'AI Paraphraser',
        seoDescription: `Paraphrasing content is essential for marketers. It helps them avoid plagiarism, improves readability, assists with conversion, and increases the SEO value of blogs and articles. It’s not just a matter of changing a few words here and there. It’s about making content more shareable with audiences. With that said, this process can be complicated. Paraphrasing what someone else has written and putting your own stamp on it is a significant time sink. It eats into your week. `,
        title: 'Paraphrase',
        example: `Peter and I had a disagreement at one point regarding a piece of writing that he thought was mine.`,
        description: 'Paraphrase your Content Instantly.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Paraphrase a paragraph of text in <>. <${args.text}>`,
                ...args,
            }),
        textLabel: 'What would you like to paraphrase?',
        textPlaceholder: 'Enter the text you’d like paraphrased here',
        showToneSelector: true,
    },
    {
        id: 'shorter',
        type: 'content',
        emoji: '✂️',
        seoTitle: 'AI text shortener',
        seoDescription:
            'Exceeding the limit or just need to cut a long story short? AI Perfect Assistant will easily focus on the main parts of the story and get rid of unnecessary details and redundancy. Stick to your line of thought without any extra twists and turns.',
        title: 'Make Shorter',
        example: `Starting a new job is like jumping into a swimming pool. A refreshing and invigorating dive can make for a memorable experience, but a belly flop can cause a lot of pain and embarrassment. Whether employees dive gracefully into a new job, or belly flop into their role, can depend on their onboarding.

        Onboarding is the crucial process through which new employees acquire the knowledge, skills, and behaviors needed to integrate into an organization. It begins with administrative tasks (such as paperwork) and first-day orientation, typically handled by HR. Then, there's role-specific training (such as shadowing another employee) and integration with the team (such as formal team introductions and informal social gatherings). This part of onboarding, which is typically handled by the new employee’s manager, can help them build relationships, understand team dynamics, and feel a part of the organization.
        
        If you're new to managing people, taking on a new employee's onboarding can be challenging. After all, you may still be figuring out your own role. But there are ways you can help your new employees have a positive onboarding experience that will set them up for long-term success on your team.`,
        description: "Get rid of the bits you don't need.",
        prompt: (args) =>
            `Make the text that I gave you in <> shorter. It should contain one or two sentences. Text: <${args.text}>`,
        textLabel: 'What would you like to make shorter?',
        textPlaceholder: 'Type here a text that you would like to make shorter',
        showToneSelector: false,
    },
    {
        id: 'translate',
        type: 'content',
        emoji: '🌐',
        seoTitle: 'AI Text Translator',
        seoDescription:
            "Get the perfect understanding of what's said or written. AI Perfect Assistant will quickly and accurately translate any piece of text into multiple languages. Do not let foreign words or texts stop you.",
        title: 'Translate',
        example: "Guten Tag, wie geht's Ihnen? ",
        description: 'Write in your preferred language.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Act as a professional translator. Translate a paragraph of text in <>. Be close to the original text. Text: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What would you like to translate?',
        textPlaceholder: 'Type here a text that you would like to translate',
        showToneSelector: true,
    },
    {
        id: 'give-definition',
        type: 'content',
        emoji: '🤓',
        title: 'Generate Definition',
        seoTitle: 'AI Tool to Generate Definitions',
        seoDescription:
            'Focus on the content, not style or structure. AI Perfect Assistant will help to organize your ideas and express thoughts clearly in couple clicks. Formal or funny- AI assistant will structure your prompt to suit the situation. Get a logically organized piece of writing devoting time to the idea, not the style.',
        example: `SOP`,
        description: 'Quickly find word definitions.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Give a definition of the word or sentence that I gave you in <>. Data: <${args.text}>`,
                ...args,
            }),
        textLabel:
            'What word or sentence you would like to get definition for?',
        textPlaceholder:
            'Type a word or sentence that you would like to get definition for',
        showToneSelector: true,
    },
    {
        id: 'improve',
        type: 'content',
        emoji: '🔨',
        title: 'Improve',
        seoTitle: 'AI tool to improve your text',
        seoDescription:
            'Focus on the content, not style or structure. AI Perfect Assistant will help to organize your ideas and express thoughts clearly in couple clicks. Formal or funny- AI assistant will structure your prompt to suit the situation. Get a logically organized piece of writing devoting time to the idea, not the style.',
        example: `Hi [Name]!

        Sorry to bother you, but I need help with [Pain Point] IMMEDIATELY!!!
        
        PLEASE GET BACK TO ME ASAP!
        
        Thanks in advance!!
        
        [Your Name]
        
        `,
        description: 'Rewrite content to make it readable.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Improve a paragraph of text in <> make it better and more readable. Topic: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What would you like to improve?',
        textPlaceholder: 'Type here a text that you would like to improve',
        showToneSelector: true,
    },
    {
        id: 'slides-outline',
        type: 'slides',
        emoji: '📚',
        seoTitle: 'AI tool to outline presentation',
        seoDescription:
            'Outline your presentation with AI Perfect Assistant to save time and ensure that your presentation is well-structured and organized.',
        title: 'Create Presentation Outline',
        example: `Create an outline of the presentation on the methods of using AI at work. Offer tips for companies from different spheres.`,
        description: 'Outline presentation in seconds.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Act as a professional slides creator. Create a plot for the presentation based on topic definition that I gave you in <>. Result should include slide number, slide title and brief description. Text: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What your presentation will be about?',
        textPlaceholder:
            'Type here a short description of slides that you would like to create',
        showToneSelector: true,
    },
    {
        id: 'text-to-slide',
        type: 'slides',
        emoji: '🎞',
        title: 'Text To Slide',
        seoTitle: 'AI Text to Slide Generator',
        seoDescription:
            'Quickly create presentations from reports, blog posts and other documents. Focus on the most relevant information to bring your ideas home to the audience clearly. AI Perfect Assistant outline the perfect slides for your presentation.',
        example: `Artificial Intelligence is poised to change teaching and learning forever
        Artificial Intelligence (AI) has the potential to revolutionize teaching and learning as we know it. By using AI-powered tools and strategies, educators can personalize learning, improve student outcomes, and better prepare students for success in the digital age. Discover how AI can transform your district, school or classroom with AI for Education.`,
        description:
            'Summarize key points of the text and convert it to a slide.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Act as a professional slides creator. Based on the text that I gave you in <> create a one slide. Result should include title of the slide and key point from the text. Text: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What text you would like to present on a slide?',
        textPlaceholder:
            'Type here a text that you would like to convert to a slide',
        showToneSelector: true,
    },
    {
        id: 'seo-title-and-description',
        type: 'content',
        emoji: '🖋',
        seoTitle: 'AI Tool to Generate Clickable Meta Titles and Descriptions',
        seoDescription: `Meta titles and descriptions are essential pieces of text that provide users and Google with more information about what your pages contain. They should be short, succinct, accurate, and to the point. However, generating great meta titles and descriptions using nothing but the power of your mind is challenging. Plus, it can be downright brain-numbing if you have hundreds or thousands to write. That’s where AI Perfect Assistant can help. This highly advanced tool uses AI to generate titles and meta descriptions in seconds. `,
        title: 'SEO Title & Meta Description',
        example: `Employee onboarding is the process by which new employees are introduced to the social and performance aspects of their new job, with the goal of getting new hires adjusted quickly, increasing productivity and job satisfaction, and reducing turnover. This process includes teaching new hires about the attitudes, competencies, company culture, employee expectations and the behaviours required to function effectively within an organisation.`,
        description: 'Write Clickable Meta Titles and Descriptions',
        prompt: (args) =>
            promptTemplate({
                prompt: `Act as a SEO (search engine optimization) professional. Came up with a title and description for a paragraph of text in <>. Text: <${args.text}>`,
                ...args,
            }),
        textLabel: 'Paste your page content here',
        textPlaceholder:
            'Describe the theme of the page and I will write the perfect SEO title and meta description.',
        showToneSelector: true,
    },
    {
        id: 'create-faq',
        type: 'content',
        emoji: '❓',
        seoTitle: 'AI FAQs Generator',
        seoDescription: `Writing an FAQ page is a powerful way to serve your customers and save yourself time. Having a list of answers reading and waiting to go reduces customer service calls and can even help your leads move past pain points. However, writing FAQs takes time. You can also sometimes miss key questions and have to go back and write them in later, which is a hassle. Fortunately, AI Perfect Assistant can help. The AI-powered tool creates a list of frequently asked questions and answers based on the content or topic you enter into the box in seconds. This way, you don’t need to go through the tedium of doing it yourself. `,
        title: 'Create FAQs',
        example: `FAQs for the HR Department of a Finance Company`,
        description: 'Create FAQs for your Content',
        prompt: (args) =>
            promptTemplate({
                prompt: `Create a list of frequently asked questions and answers based on the content that i gave you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'Describe your topic, or paste in some content',
        textPlaceholder:
            'Describe your topic, or paste in some content, and I will create you FAQs to related to the topic.',
        showToneSelector: true,
    },
    {
        id: 'blog-post-intro',
        type: 'content',
        emoji: '🚀',
        seoTitle: 'AI Blog Post Intros Generator',
        seoDescription: `Introducing your blogs in an exciting way is essential for getting visitors and clicks. If you get the formula right, you can drive interest and generate tremendous engagement. However, writing a compelling introduction to a post isn’t easy. Writer’s block is real! With AI Perfect Assistant, though, you don’t have to do all this yourself. Instead, the tool does it for you. Just enter the blog title or topic and it’ll spit back text introducing your post, telling readers what they can expect.`,
        title: 'Blog Post Intro',
        example: `Blog post about promoting your Instagram account`,
        description: 'Generate your Blog Post Intros',
        prompt: (args) =>
            promptTemplate({
                prompt: `Create a blog post intro based on the blog post title that i gave you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'Enter your blog post title',
        textPlaceholder: `Enter your blog post title, and I'll write an excellent intro that will lure readers into the rest of the post.`,
        showToneSelector: true,
    },
    {
        id: 'blog-post-brief',
        type: 'content',
        emoji: '📑',
        seoTitle: 'AI Content Brief Generator',
        seoDescription: `Creating accurate, actionable briefs for your writers is essential. With the proper information, they can craft the content you need, without the need for constant revisions and disappointed clients. However, creating briefs is an art form in its own right. You need to communicate essential information without overwhelming the writer with unnecessary data. That’s where AI Perfect Assistant can help. This simple tool lets you create concise, easy-to-follow briefs in seconds, improving the likelihood your writers will produce the high-quality content you want.`,
        title: 'Blog Post Brief',
        example: `Blog post about promoting your Instagram account`,
        description: 'Generate Content Briefs for your Blog',
        prompt: (args) =>
            promptTemplate({
                prompt: `Create a blog post brief based on the blog post definition that i gave you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: `What blog post you'd like to write?`,
        textPlaceholder: `Describe the blog post you'd like to write, including any sub topics and keywords and I will create a blog post brief.`,
        showToneSelector: true,
    },
    {
        id: 'continue-sentence',
        type: 'content',
        emoji: '▶️',
        seoTitle: 'AI tool to continue sentence',
        seoDescription: `Enhance your writing seamlessly with AI Perfect Assistant, powered by ChatGPT and integrated across Microsoft 365 and Chrome. Perfect for Word, PowerPoint, Outlook, or your browser, our app helps you effortlessly complete sentences, enriching your documents and emails with coherent, contextually relevant content. Ideal for writers, students, and professionals seeking to streamline their writing process and boost productivity.`,
        title: 'Continue Sentence',
        example: `AI assistant is a great thing to...`,
        description: 'Enhance your writing seamlessly',
        prompt: (args) =>
            promptTemplate({
                prompt: `Continue text that I gave you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: `What sentence or paragraph you would like to continue?`,
        textPlaceholder: `Type sentence or paragraph that you would like to continue.`,
        showToneSelector: true,
    },
    {
        id: 'blog-post-generator',
        type: 'content',
        emoji: '🤖',
        seoTitle: 'AI Blog Post Generator',
        seoDescription: `Writing blog posts is a great way to communicate with your target audience, drive sales, boost your personal brand, and generate authority. As such, it is an essential pillar of effective marketing, both corporate and personal. However, getting it right is not easy. It takes a long time to craft compelling text, and writing doesn’t come naturally to many people. That’s where AI Perfect Assistant can help. The tool can generate entire blog posts for you in seconds, potentially saving you countless hours at your computer.`,
        title: 'Blog Post Generator',
        example: `Blog post about promoting your Instagram account`,
        description: 'Write Unlimited Blog Posts',
        prompt: (args) =>
            promptTemplate({
                prompt: `Create a blog post based on the brief blog post description that i gave you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: `What blog post you'd like to write?`,
        textPlaceholder: `Describe what you would like me to write about and I will write a blog post for you.`,
        showToneSelector: true,
    },
    {
        id: 'title',
        type: 'content',
        emoji: '🥇',
        seoTitle: 'AI Title and Headings Generator',
        seoDescription:
            'Create eye-catchy, engaging and emotionally compelling titles for your article, story or any piece of witting. AI Perfect Assistant will offer you numerous variants of the title that would help you engage with the readers, raise interest to the topic and keep everyone in suspense.',
        title: 'Generate Title for Text',
        example: `Employee onboarding is the process by which new employees are introduced to the social and performance aspects of their new job, with the goal of getting new hires adjusted quickly, increasing productivity and job satisfaction, and reducing turnover.

        This process includes teaching new hires about the attitudes, competencies, company culture, employee expectations and the behaviours required to function effectively within an organisation.`,
        description: 'Generate a title for the given text.',
        prompt: (args) =>
            promptTemplate({
                prompt: `Came up with a title for a paragraph of text in <>. Text: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What text should we generate title for?',
        textPlaceholder:
            'Type here a text for which you would like to generate a title',
        showToneSelector: true,
    },
    {
        id: 'instagram-captions',
        type: 'social media',
        emoji: '📸',
        seoTitle: 'AI Instagram Captions Generator',
        seoDescription: `Instagram captions are text that goes with posts on Instagram. Brands use them to convey their personality, communicate with their audience, and add context to their images and videos. While generating captions is straightforward, it can be time-consuming, particularly if you have hundreds of them. Fortunately, AI Perfect Assistant makes it easy. Just tell it what your Instagram post is about, and it’ll give you exciting text snippets, complete with hashtags, in seconds.`,
        title: 'Instagram Captions',
        example: `Post about a new pet - a little French bulldog.`,
        description: 'Generate Enticing Instagram Captions',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a list of interesting captions with relevant hashtags for Instagram post based on the post description that I provided you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your post about?',
        textPlaceholder: `Describe what your Instagram post is about and I will create a list of interesting captions with relevant hashtags.`,
        showToneSelector: true,
    },
    {
        id: 'social-media-post-ideas',
        type: 'social media',
        emoji: '💡',
        seoTitle: 'AI tool to generate Creative Social Media Post',
        seoDescription: `Writing engaging content for social media is essential for brand campaigns. Quality posts help you build a substantial following and get people interested in what you do. With that said, it’s not always easy to come up with fresh ideas every day. You might struggle to think of topics from new angles or generate engaging headlines to share with followers. With AI Perfect Assistant, though, you can generate social media post ideas in seconds.`,
        title: 'Social Media Post Ideas',
        example: `Local coffee shop`,
        description: 'Generate Creative Social Media Post Ideas',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a list of awesome social media post ideas based on the description that I provided you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your topic or industry?',
        textPlaceholder: `Describe your industry or topic and I will create a list of awesome social media post ideas.`,
        showToneSelector: true,
    },
    {
        id: 'blog-post-to-tweet-thread',
        type: 'social media',
        emoji: '🔀',
        seoTitle: 'AI Twitter Threads Generator',
        seoDescription: `Tweet threads are a great way to drive engagement on Twitter and establish your brand as an authority in your niche. However, creating them is time-consuming. You have to trawl through reams of existing content and edit it line-by-line to get something that will fit the social media platform’s required format. Fortunately, with AI Perfect Assistant, you can bypass that hassle.`,
        title: 'Blog Post To Tweet Thread',
        example: `In this blog post I would like to share with you how I launched my first coffee shop.`,
        description: 'Create Twitter Threads from your Blog Posts',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a twitter thread based on the content that I provided you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'Paste your blog post or other content',
        textPlaceholder: `Paste your blog post content here and I will create a tweet thread based on the content.`,
        showToneSelector: true,
    },
    {
        id: 'tiktok-or-instagram-reel-ideas',
        type: 'social media',
        emoji: '🤳',
        seoTitle: 'AI TikTok or Instagram Reel Generator',
        seoDescription: `Reels are short videos up to 60 seconds long hosted on Instagram. They are a valuable way for brands to express creativity, showcase their products, or simply have fun. Most marketers can think of a dozen or so Reel ideas for their brand. However, coming up with new concepts every day for years on end is challenging. Eventually, the barrel runs dry. That’s where AI Perfect Assistant can help. It can generate thousands of Instagram Reel concepts in a flash, giving you content ideas that could last for years to come. All you need to do is tell it your industry or niche and provide additional details, and it’ll do the rest of the hard work for you.`,
        title: 'TikTok or Instagram Reel Ideas',
        example: `Teaching foreign languages to students`,
        description: 'Generate Creative TikTok or Instagram Reel Ideas',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a list of tiktok or instagram reel ideas based on the content that I provided you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your niche / subject?',
        textPlaceholder: `Describe the niche / subject you want me to suggest reel video ideas for. The more specific you are the more related my suggestions will be.`,
        showToneSelector: true,
    },
    {
        id: 'content-calendar',
        type: 'social media',
        emoji: '📆',
        seoTitle: 'AI Content Calendar Generator',
        seoDescription: `Creating content calendars is essential for anyone looking to build a comprehensive marketing campaign for their brand or business. Schedules help keep the ideas flowing, letting you continue providing your audience with value. However, developing a content calendar from scratch takes a long time. Plus, you might run out of ideas. That’s where AI Perfect Assistant can help. This useful tool can create entire month’s calendars for you from a single keyword. Just provide it with information about your topic, business, or niche, and it’ll provide a huge selection of ideas in seconds. `,
        title: 'Content Calendar',
        example: `Business: Local coffee shop. Marketing calendar for the month of December, include shootings, blog posts and marketing events. `,
        description: 'Generate your Content Calendar',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a content calendar for a month based on the description that I provided you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'Enter keywords for content calendar',
        textPlaceholder: `Simply enter your keyword here. From your keyword I'll create you a content calendar for a month.`,
        showToneSelector: true,
    },
    {
        id: 'tweet-ideas',
        type: 'social media',
        emoji: '🦆',
        seoTitle: 'AI Tweet Generator',
        seoDescription: `Tweets are a fantastic way to reach out to your target audience. Little snippets and thoughts keep them hooked on your brand, whether you’re a one-person business or a multi-billion-dollar enterprise. Cooking up new tweets every few hours, though, isn’t easy. Constantly thinking of new things to say takes effort, and you can easily wind up repeating yourself. That’s where AI Perfect Assistant can help. You just enter a topic you want to talk about and the algorithm generates fully-fledged tweets you can post in seconds.`,
        title: 'Tweet Ideas',
        example: `Tweet ideas for foreign language teachers.`,
        description: 'Generate Tweet Ideas',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a tweet ideas based on the content that I provided you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'Enter your topic or industry here',
        textPlaceholder: `Enter your topic or industry here and I'll create you a list of tweets for your audience.`,
        showToneSelector: true,
    },
    {
        id: 'tiktok-instagram-reel-script',
        type: 'social media',
        emoji: '🎬',
        seoTitle: 'AI TikToks or Instagram Reels Scripts Generator',
        seoDescription: `Developing great TikTok and Instagram Reels scripts is essential for any effective marketing campaign, whether you’re a solo entrepreneur or a major brand. It helps you build awareness, grow your brand and impact your audience faster. Unfortunately, sitting down and writing scripts takes a long time – potentially several hours every day. Plus, it’s hard to constantly generate new ideas. That’s where AI Perfect Assistant can help. This incredible tool generates relevant scripts in seconds based on simple prompts.`,
        title: 'TikTok or Instagram Reel Script',
        example: `Reel about first day on a work as a teacher`,
        description: 'Generate Scripts for TikToks or Instagram Reels',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate Script for TikTok or Instagram Reel based on the content that I provided you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'Enter your topic and any notes',
        textPlaceholder: `Enter your topic and any notes, and I'll create your script.`,
        showToneSelector: true,
    },
    {
        id: 'facebook-ads',
        type: 'advertisement',
        emoji: '👥',
        seoTitle: 'AI Facebook / Meta Ads Generator',
        seoDescription: `For most brands, the ability to create and post Facebook ads is absolutely essential. Companies need to reach out on social media to raise awareness and build deeper and more meaningful connections with their audiences. Unfortunately, generating large volumes of compelling Facebook ad copy is time-consuming and expensive. It takes ages to get it right. Plus, without relevant ad copy, you can wind up targeting the wrong audience. With AI Perfect Assistant, you avoid these issues.`,
        title: 'Facebook Ads',
        example: `An ad of a new sports and wellness centre in the local area`,
        description: 'Create Your Facebook Ads in Seconds',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a list of facebook ads based on the ad description that I provided you in []. Every ad should have Primary Text, Headlines and Description. [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your ad about?',
        textPlaceholder: `Describe what you want your ads to be about with as much detail as you like and I will create Facebook ads with Primary Text, Headlines and Descriptions.`,
        showToneSelector: true,
    },
    {
        id: 'google-ads',
        type: 'advertisement',
        emoji: '📢',
        seoTitle: 'AI Google Ads Generator',
        seoDescription: `Writing great ad copy is a near-guaranteed way to get people to find your business on Google. The right combination of catchy text, brand name mentions, and special offers can entice even the most resistant of buyers. However, figuring out what you should say in ad copy is time-consuming and, sometimes, really hard. It takes ages to come up with an idea, and once you have one, you have to play around with it multiple times to get it right. The constant tweaking can drive you up the wall. But with AI Perfect Assistant, you can build user-converting, click-generating Google ad copy instantly.`,
        title: 'Google Advertising Copy',
        example: `Brand: Pet.co, theme: supplies for pets`,
        description: 'Write Your Google Ads in Seconds',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a list of google ads based on the ad description that I provided you in []. Every ad should have a few Headlines and Description. [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your ad about?',
        textPlaceholder: `Enter your brand and adgroup theme, and I'll create headlines and descriptions for your Ad Group.`,
        showToneSelector: true,
    },
    {
        id: 'marketing-campaign-ideas',
        type: 'business',
        emoji: '💡',
        seoTitle: 'AI Marketing Campaign Ideas Generator',
        seoDescription: `The world's leading companies are always coming up with new marketing strategies. Their ingenuity is evident in their financial success. It takes a special kind of creativity. That's where AI Perfect Assistant comes in. This marketing campaigns ideas generator can produce marketing campaign ideas quickly and easily.`,
        title: 'Marketing Campaign Ideas',
        example: `Knowledge base SaaS solution`,
        description: 'Develop new marketing campaigns for your business',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a list of marketing campaign ideas based on a business definition that I gave you in the []: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your business about?',
        textPlaceholder: `Please describe your business here`,
        showToneSelector: true,
    },
    {
        id: 'market-research',
        type: 'business',
        emoji: '🔍',
        seoTitle: 'AI Market Research Generator',
        seoDescription: `Figuring out who your audience is and what they want is business 101. You need to know who you’re selling to if you want to make money. 
            Unfortunately, while that process might sound simple in theory, it is more challenging in practice. Old-fashioned research takes a lot of time, money, and energy.            
            That’s where AI Perfect Assistant can help. It’ll tell you who your audience is, their demographic, and how you can learn more about them in seconds. `,
        title: 'Market Research',
        example: `Knowledge base SaaS solution`,
        description: 'Conduct Market Research Instantly',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a detailed market research report for the industry in the []: [${args.text}]`,
                ...args,
            }),
        textLabel: 'Type your industry here',
        textPlaceholder: `Please enter your industry here, and I'll conduct market research and give you the ideal demographic for your business and how to reach them.`,
        showToneSelector: true,
    },
    {
        id: 'sop-generator',
        type: 'business',
        emoji: '📝',
        seoTitle: 'AI SOPs or Standard Operating Procedure Generator',
        seoDescription:
            'Creating effective SOPs is a critical part of running a successful business. It is important for employees to understand the correct order of tasks to ensure they are working efficiently and safely. Unfortunately, writing SOPs can be a lengthy process, especially if there are many roles in the organization. AI Perfect Assistant is ready to help! Its SOP generator will create the SOP in seconds.',
        title: 'Generate SOP',
        example: `Customer Service SOP`,
        description: 'Create effective SOPs in seconds',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a detailed Standard Operating Procedure (SOP) based on the following brief description: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What SOP you would like to generate?',
        textPlaceholder: 'Type here a brief description of SOP',
        showToneSelector: true,
    },
    {
        id: 'review-generator',
        type: 'business',
        emoji: '⭐️',
        seoTitle: 'AI product reviews generator',
        seoDescription:
            'Elevate your online presence with AI Perfect Assistant, integrated with Microsoft 365 and Chrome. Effortlessly craft engaging product or service reviews in Word, PowerPoint, Outlook, or your browser. Perfect for businesses aiming to attract customers and boost visibility.',
        title: 'Review your product or service',
        example: `SaaS app that generates resume with help of chatGPT`,
        description: 'Effortlessly craft engaging product or service reviews',
        prompt: (args) =>
            promptTemplate({
                prompt: `Act as a human who used product or service that i gave you in []. Generate a detailed product or service review based on the on the  brief description that i gave you in []. Description: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your product or service about?',
        textPlaceholder: `Please describe your product or service here`,
        showToneSelector: true,
    },
    {
        id: 'brand-name-generator',
        type: 'business',
        emoji: '🤖',
        seoTitle: 'AI Brand Names Generator',
        seoDescription: `Cooking up great brand names helps companies thrive. They should be short, relevant, and, sometimes, a little quirky. However, as any marketer or entrepreneur knows, generating ideas is challenging. It can take weeks before you finally arrive at something that works. That’s where AI Perfect Assistant can help. `,
        title: 'Brand Name Generator',
        example: `Generate a name for AI powered assistant`,
        description: 'Generate Recognizable Brand Name',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a list of brand names based on the following brief description: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your business about?',
        textPlaceholder: `Please describe your business here`,
        showToneSelector: true,
    },
    {
        id: 'company-profile',
        type: 'business',
        emoji: '🗃',
        seoTitle: 'AI Company Profile Generator',
        seoDescription: `Craft compelling company profiles effortlessly with AI Perfect Assistant, integrated into Microsoft 365 and available as a Chrome extension. Elevate your business presence by transforming basic info into professional profiles in Word, PowerPoint, Outlook, and directly in your browser. Ideal for any business aiming for impact.`,
        title: 'Company Profile Generator',
        example: `Company with the name "Microsoft". Founded in 1989. Developing software.`,
        description:
            'Elevate your business presence by transforming basic info into professional profiles',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a company profile template based on the data that i gave you in <>. If you do not have enough data leave placeholders with guidance. Company info: <${args.text}>`,
                ...args,
            }),
        textLabel: 'What is your business about?',
        textPlaceholder: `Please describe your business here`,
        showToneSelector: true,
    },
    {
        id: 'video-script-generator',
        type: 'video',
        emoji: '🎥',
        seoTitle: 'AI Video Script Generator',
        seoDescription: `Writing a script for your video campaigns is a no-nonsense way to ensure your message follows a logical sequence. After all, if you’re a brand, influencer, or professional, you want viewers to follow your arguments, and understand your points. Getting this approach right, though, is time-consuming. Writing comprehensive video scripts is often an enormous project that takes great chunks of time out of your week. Surely, there’s a better way. That’s where AI Perfect Assistant can help. You just describe your video’s theme and some of the points you’d like to make and it’ll create a script for you in seconds.`,
        title: 'Generate Video Script',
        example: `Advertisement video for coffee shop`,
        description: 'Instantly Generate Video Scripts',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a video script based on a brief description that I gave you in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your video about?',
        textPlaceholder: `Describe the theme of the video and some points you'd like to make, and I will create a video script.`,
        showToneSelector: true,
    },
    {
        id: 'youtube-titles-generator',
        type: 'video',
        emoji: '🍿',
        seoTitle: 'AI Youtube Video Title Generator',
        seoDescription: `Pretty much all successful YouTube channels rely on clickbait titles to get views. They know if they inject enough intrigue, controversy, or insight into their headlines, they’ll get views. With that said, cooking up click-worthy YouTube titles is anything but straightforward. You need to have a certain kind of mind to do it, and even then, there will still be days when you struggle. Fortunately, AI Perfect Assistant is here to help.`,
        title: 'Generate YouTube Title',
        example: `Video that presents a new app for iPhone`,
        description: 'Generate Clickbait Titles for YouTube Videos',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a list of click-bait Youtube titles for the video described in []. [${args.text}]`,
                ...args,
            }),
        textLabel: 'What is your video about?',
        textPlaceholder: `Describe the theme of the video and I will create a list of click-bait YouTube titles.`,
        showToneSelector: true,
    },
    {
        id: 'query-chat-gpt',
        type: 'advanced',
        emoji: '💭',
        seoTitle: 'Query ChatGPT',
        seoDescription:
            'So many questions with no answers? Query ChatGPT right from AI Perfect Assistant!',
        title: 'Query ChatGPT',
        example: `What are the differences between IOS and Android?`,
        description: 'Write any query to ChatGPT and get response.',
        textLabel: 'Write your query',
        textPlaceholder: 'Type any query to ChatGPT',
        showToneSelector: false,
        prompt: ({ text }) => text,
    },
    {
        id: 'generate-excel-formula',
        type: 'spreadsheets',
        emoji: '➕',
        seoTitle: 'AI Excel Formula Generator',
        seoDescription: `Discover the power of our AI Excel Formula Generator, designed to streamline your spreadsheet tasks and boost productivity. Effortlessly create complex formulas and optimize your Excel experience with cutting-edge AI technology.`,
        title: 'Generate Excel Formula',
        example: `Calculate the product of values in Column C and D`,
        description: 'Generate Excel formulas like a PRO',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate an Excel formula based on user input in []. Result should contain only formula no other information, so user could easily copy and paste it to excel.  [${args.text}]`,
                ...args,
            }),
        textLabel: 'What your formula should do?',
        textPlaceholder: `Briefly explain what formula you are looking for.`,
        showToneSelector: false,
    },
    {
        id: 'explain-excel-formula',
        type: 'spreadsheets',
        emoji: '🧮',
        seoTitle: 'AI Excel Formula Explainer',
        seoDescription: `Discover how our AI Excel Formula Explainer can simplify complex formulas and boost your productivity. Perfect for professionals looking to master Excel with ease.`,
        title: 'Explain Excel Formula',
        example: `=SUM(1,2,3,4)`,
        description: 'Simplify complex Excel formulas',
        prompt: (args) =>
            promptTemplate({
                prompt: `Explain an Excel formula that user type in []. Result should contain explanation following next template 1. Brief explanation of what is inside formula 2. Example on how it works 3. Potential issues if any 4. Conclusion.  [${args.text}]`,
                ...args,
            }),
        textLabel: 'Type in formula to explain',
        textPlaceholder: `Type in formula you need help with`,
        showToneSelector: true,
    },
    {
        id: 'humanize-text',
        type: 'content',
        emoji: '👨‍⚖️',
        seoTitle: 'AI Tool to humanize text produced by AI',
        seoDescription: `Is it too obvious that your copy is AI written? Not a problem! Add some human touch to it to make it more readable, naturally structured to pass any checks easily.`,
        title: 'Humanize AI Text',
        example: `In the ever-evolving landscape of artificial intelligence, the profound impact it continues to have on various sectors is undeniably transformative. AI, with its unparalleled ability to process vast amounts of data at lightning speed, is not only reshaping industries but also redefining human interaction with technology. By harnessing the power of machine learning algorithms, businesses can now make data-driven decisions with unprecedented accuracy, leading to enhanced efficiency and innovation. As AI continues to advance, it promises to unlock new possibilities and solutions, paving the way for a future where the synergy between human ingenuity and artificial intelligence sets the stage for groundbreaking achievements.`,
        description:
            'Transform AI-generated text to make it more human-like and bypass AI detection',
        prompt: (args) =>
            promptTemplate({
                prompt: `Take the following AI-generated text I submitted to you in [] and make it sound more natural, engaging, and human-like. Improve clarity, add a conversational tone if appropriate, and ensure it flows smoothly while maintaining the original meaning. Avoid robotic phrasing and repetitive structures. Text to refine: [${args.text}]`,
                ...args,
            }),
        textLabel: 'Paste text generated by AI',
        textPlaceholder: `Paste AI generated text that you would like to humanize`,
        showToneSelector: true,
    },
    {
        id: 'brainstorm-tool',
        type: 'business',
        emoji: '🧠',
        seoTitle: 'AI Brainstorming Tool',
        seoDescription: `Boost you problem-solving skills with our AI Assistant. We'll give you a hand with any problem: from a minor to a real crucial one. Find swift fixes for any issue you face.`,
        title: 'Brainstorming Tool',
        example: `Brainstorm 5 solutions for sustainable energy case.`,
        description: 'Generate creative solutions and ideas for any problem',
        prompt: (args) =>
            promptTemplate({
                prompt: `Analyze the following problem i gave you in [] and brainstorm possible solutions. Consider different angles, creative approaches, and practical strategies to address it. Provide insights, potential challenges, and recommendations. Problem: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What problem you would like to brainstorm?',
        textPlaceholder: `Type in what problem you would like to brainstorm`,
        showToneSelector: true,
    },
    {
        id: 'startup-ideas',
        type: 'business',
        emoji: '💯',
        seoTitle: 'AI Startup Ideas Generator',
        seoDescription: `Want to start a business and do not know where to begin? Our AI model is here to help. We'll create a set of startup ideas for any area that will blow your audience's mind. With us - you'll never miss the goal.`,
        title: 'Startup Ideas Generator',
        example: `E-Commerce in Brazil`,
        description: 'Generate your next startup idea',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate innovative startup ideas based on the following brief instructions i gave you in []. Consider market demand, scalability, and unique value propositions. Provide a variety of ideas, including tech-driven, service-based, and niche opportunities. Instructions: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What startup you would like to launch?',
        textPlaceholder: `Type in a few words on your expertise or field where you would like to launch a startup`,
        showToneSelector: true,
    },
    {
        id: 'cta-generator',
        type: 'content',
        emoji: '📢',
        seoTitle: 'AI Call to Action Generator',
        seoDescription: `Creating compelling calls to action in seconds is an art that can significantly enhance your engagement and conversion rates. By swiftly tapping into the emotional and psychological triggers of your audience, you can craft CTAs that resonate deeply and inspire immediate action. Let AI help you to succeed!`,
        title: 'Call to Action Generator',
        example: `Generate compelling CTA for a button on landing page of an AI powered tool`,
        description: 'Generate compelling CTAs in seconds',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a compelling and concise call-to-action (CTA) label based on the following context. Ensure it is engaging, action-oriented, and aligned with the intended user behavior. Context: [${args.text}]`,
                ...args,
            }),
        textLabel: 'Where your CTA will be placed?',
        textPlaceholder: `Type in a few words describing context of your CTA`,
        showToneSelector: true,
    },
    {
        id: 'speech-writer',
        type: 'content',
        emoji: '📣',
        seoTitle: 'AI Speech Writer',
        seoDescription: `Create a heart-warming or persuasive speech? Having a lecture or an important meeting? Our AI Assistant is here to help. Get the perfect speech that will reach your audience and deliver your ideas the best way possible.`,
        title: 'Speech Writer',
        example: `friendly graduation day speech for students`,
        description: 'Generate a compelling speech for any occasion',
        prompt: (args) =>
            promptTemplate({
                prompt: `Write a compelling speech based on the following context i gave you in []. Ensure it is engaging, well-structured, and suited for the intended audience and purpose. Use a natural, persuasive tone and include key points that reinforce the main message. Context: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What speech you would like to generate?',
        textPlaceholder: `Type in a few words describing context of your speech`,
        showToneSelector: true,
    },
    {
        id: 'interview-questions-generator',
        type: 'business',
        emoji: '❓',
        seoTitle: 'AI Interview Questions Generator',
        seoDescription: `Having a job interview or any other admission process? Get ready with AI to hit the goal and answer even the trickiest question of your interviewee.`,
        title: 'Interview Questions Generator',
        example: `Interview questions for a position of a Customer Support manager`,
        description: 'Make your next interview preparation a breeze',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a set of insightful interview questions based on the following context. Ensure the questions are relevant, thought-provoking, and help uncover meaningful insights. Adapt the tone and depth based on the subject matter. Context: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What interview questions you would like to generate?',
        textPlaceholder: `Type in a few words describing context of your interview`,
        showToneSelector: true,
    },
    {
        id: 'okr-generator',
        type: 'business',
        emoji: '❓',
        seoTitle: 'AI OKR Generator',
        seoDescription: `Need to create a detailed plan to achieve your goal? Generate key results with AI to get a step-by-step plan for any idea you have to make it come true.`,
        title: 'OKR Generator',
        example: `Generate OKR to increase the revenue by 100% in 12 months`,
        description:
            'OKR Generator helps you create Key Results for your objectives',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate clear and measurable OKRs (Objectives and Key Results) based on the following goal. Ensure the objectives are ambitious yet achievable, and the key results are specific, trackable, and outcome-driven. Goal: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What goal you want to hit?',
        textPlaceholder: `Type in a few words describing context of your goal`,
        showToneSelector: true,
    },
    {
        id: 'headline-generator',
        type: 'content',
        emoji: '📰',
        seoTitle: 'AI Headline Generator',
        seoDescription: `Let AI create a headline that will grab you audience's attention in no time. Eye-catchy, intriguing and brain-wrecking title for you posts, articles and many more.`,
        title: 'Headline Generator',
        example: `Generate a thought-provoking headline for an article on ecology`,
        description: 'Generate eye-catching headlines for any purpose',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate eye-catching and compelling headlines based on the following context. Ensure they are attention-grabbing, engaging, and aligned with the intended message and audience. Context: [${args.text}]`,
                ...args,
            }),
        textLabel: 'What headlines you would like to generate?',
        textPlaceholder: `Type in a few words describing context of your headline`,
        showToneSelector: true,
    },
    {
        id: 'meeting-minutes-generator',
        type: 'business',
        emoji: '📝',
        seoTitle: 'AI Meeting Minutes Generator',
        seoDescription: `All your meeting notes look messy and disorganized after a brainstorm or an active discussion? Let AI structure all the ideas, decisions and insights you cam up with into a logical and clear-cut structure.`,
        title: 'Meeting Minutes Generator',
        example: `-project timelines, resource allocation, and budget considerations. 
-current progress: noting that several milestones successfully achieved,
-a few areas need focus to stay on track 
-adjustments to the resource allocation might be necessary in the software dev department, to meet upcoming deadlines
-finance team  updated budget analysis with a slight increase in costs due to expenditures`,
        description:
            'Generate concise and organized meeting minutes from your notes',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate well-structured and professional meeting minutes based on the following meeting notes. Ensure clarity, include key discussions, decisions made, action items, and next steps. Keep the format concise and easy to follow. Meeting notes: [${args.text}]`,
                ...args,
            }),
        textLabel: 'Paste your meeting notes below',
        textPlaceholder: `Type in or paste your meeting notes here`,
        showToneSelector: true,
    },
    {
        id: 'keywords-generator',
        type: 'business',
        emoji: '📝',
        seoTitle: 'AI Keywords Generator',
        seoDescription: `Generate topical keywords for any purpose: a blog post on Facebook, a scientific article, a YouTube video. No matter what you're working on, we got you! AI Assistant will extract most helpful and efficient keyword out of your text. `,
        title: 'Keywords Generator',
        example: `keywords for an e-commerce store located in Brazil that sell t-shirts`,
        description:
            'Generate relevant keywords from your input text to boost SEO and content relevance',
        prompt: (args) =>
            promptTemplate({
                prompt: `Generate a list of relevant and high-impact keywords based on the following input. Ensure they are optimized for SEO and content discoverability on social networks like Facebook, YouTube, and other platforms. Include a mix of short-tail and long-tail keywords, trending terms, and variations for maximum reach. Input: [${args.text}]`,
                ...args,
            }),
        textLabel: 'Describe a topic for keywords',
        textPlaceholder: `Briefly describe a topic on what keywords should be generated`,
        showToneSelector: true,
    },
    {
        id: 'ocr-text',
        type: 'business',
        emoji: '🕵',
        seoTitle: 'AI Powered OCR (Optical Character Recognition)',
        seoDescription: `Extract text from images and scanned documents instantly with our AI-powered OCR tool. Fast, accurate, and supports multiple languages.`,
        title: 'OCR (Optical Character Recognition)',
        example: `(OCR)`,
        description:
            'Extract text from images and scanned documents instantly with our AI-powered OCR tool',
        prompt: (args) =>
            promptTemplate({
                prompt: `Extract all visible text from the provided image without modification or interpretation. Provide the extracted content exactly as it appears in raw text format. Do not include additional commentary or formatting. If no text found return "I did not find any text"`,
                ...args,
            }),
        textLabel: '-',
        textPlaceholder: `-`,
        showToneSelector: false,
    },
];
